// Editor del Prompt del Agente (capa `base` por organización) — admin-admin.
// Editar → Guardar crea una versión NUEVA y la activa al instante (efecto al
// siguiente mensaje, sin desplegar). Historial + Restaurar = rollback a la
// "versión funcional". El backend (Orión /prompts/org-base*) gatea admin/dev/cto
// y valida multi-tenant; aquí solo renderizamos. Tokens semánticos (nunca colores
// crudos): bg-card / text-foreground / border-border / text-primary…
import { useCallback, useEffect, useState } from 'react';
import { History, RotateCcw, Save, Wand2, Loader2, Check, AlertCircle } from 'lucide-react';
import { promptApi, type PromptLayer, type PromptVersion } from './api';
import { OsApp } from '@/modules/orion-shell/OsApp';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/** `embedded`: dentro de otra app del SO (Configuración) — sin su propia ventana. */
export function PromptEditorPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [current, setCurrent] = useState<PromptLayer | null>(null);
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [base, hist] = await Promise.all([promptApi.getBase(), promptApi.getHistory(40)]);
      setCurrent(base.prompt);
      setContent(base.prompt?.content ?? '');
      setVersions(hist.versions ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el prompt');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = content.trim() !== (current?.content ?? '').trim();
  const tooShort = content.trim().length < 10;

  async function save() {
    if (!dirty || tooShort || busy) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const res = await promptApi.saveBase(content);
      setOk(`Guardado — versión ${res.prompt.version} activa (efecto al siguiente mensaje).`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar');
    } finally {
      setBusy(false);
    }
  }

  async function rollback(v: PromptVersion) {
    if (v.is_active || busy) return;
    if (
      !window.confirm(
        `¿Restaurar la versión ${v.version}? Se convertirá en la versión activa del agente ` +
          `(efecto al siguiente mensaje). La versión actual queda en el historial.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const res = await promptApi.rollback(v.id);
      setOk(`Versión ${res.prompt.version} reactivada — ahora es la activa.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo restaurar');
    } finally {
      setBusy(false);
    }
  }

  const subtitle = current ? `v${current.version} activa · cada guardado es una versión nueva` : 'Instrucción base del agente de tu organización';
  const body = (
    <div>
      {embedded && <p className="mb-3 text-xs text-muted-foreground">{subtitle}</p>}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {ok && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-primary">
          <Check size={16} className="mt-0.5 shrink-0" />
          <span>{ok}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
          {/* Editor */}
          <div className="rounded-xl border border-border bg-card p-4">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck={false}
              className="h-[520px] w-full resize-y rounded-lg border border-border bg-background p-3 font-mono text-[13px] leading-relaxed text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Instrucción base del agente…"
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void save()}
                disabled={!dirty || tooShort || busy}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                Guardar (nueva versión)
              </button>
              <span className="text-xs text-muted-foreground">
                {content.length.toLocaleString('es-MX')} caracteres
                {dirty ? ' · cambios sin guardar' : ''}
                {tooShort ? ' · demasiado corto (mín. 10)' : ''}
              </span>
            </div>
          </div>

          {/* Historial */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <History size={16} className="text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">Historial de versiones</h2>
            </div>
            <div className="flex max-h-[520px] flex-col gap-2 overflow-auto">
              {versions.length === 0 && (
                <p className="text-xs text-muted-foreground">Sin versiones previas.</p>
              )}
              {versions.map((v) => (
                <div
                  key={v.id}
                  className={`rounded-lg border px-3 py-2 ${
                    v.is_active ? 'border-primary/50 bg-primary/5' : 'border-border bg-background'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-foreground">v{v.version}</span>
                    {v.is_active ? (
                      <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                        activa
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void rollback(v)}
                        disabled={busy}
                        className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
                      >
                        <RotateCcw size={12} /> Restaurar
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {fmtDate(v.updated_at || v.created_at)} · {v.content_length.toLocaleString('es-MX')} ch
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return body;
  return (
    <OsApp icon={Wand2} title="Prompt del Agente" subtitle={subtitle}>
      {body}
    </OsApp>
  );
}
